/**
 * Screen 1 of the self-service onboarding flow: "Create your account".
 *
 * Rendered inside AuthLayout (same shell as the login screen). Each provider
 * button runs the start → OAuth sequence: the Turnstile token (when captcha
 * is configured) is POSTed to `/self-service/start/` to arm the session, the
 * wizard's first step is stashed as the post-OAuth return URL (consumed by
 * `ProviderCallback`), and the browser is handed to allauth's provider login.
 *
 * Below the providers, the email form runs the same start sequence and then
 * signs up through allauth headless with just the email. That leaves a pending
 * verify_email flow, which the global AuthChangeRedirector routes to
 * /account/verify-email (the code-entry screen); the stashed handoff sends the
 * user to the set-password step once the code logs them in.
 */
import type { FormEvent } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthProcess, signUp } from '@/lib/allauth';
import { validateEmail } from '@/lib/auth/constants';
import {
  clearEmailVerificationHandoff,
  stashEmailVerificationHandoff,
} from '@/lib/auth/emailVerification';
import { getProviderButtonLabel } from '@/lib/auth/provider-labels';
import { Button } from '@/lib/base/buttons/button';
import { HintText, useFormValidation } from '@/lib/base/forms';
import { Input } from '@/lib/base/forms/input/input';
import {
  useSelfServiceStatus,
  useStartSelfServiceSignup,
} from '@/lib/self-service/api';
import { TurnstileWidget } from '@/lib/self-service/components/TurnstileWidget';
import {
  FIRST_STEP_PATH,
  SET_PASSWORD_PATH,
} from '@/lib/self-service/constants';
import ProviderList from '@/pages/auth/ProviderList';

const OAUTH_CALLBACK_PATH = '/account/provider/callback';
/** sessionStorage key `ProviderCallback` reads to decide where to land after OAuth. */
const OAUTH_RETURN_URL_KEY = 'oauth_return_url';
const VERIFY_EMAIL_PATH = '/account/verify-email';

const EMAIL_FIELDS = {
  email: { required: true, validate: validateEmail },
} as const;

const GENERIC_SIGNUP_ERROR =
  "We couldn't start your signup. Try again, and get in touch if it keeps happening.";

export function SelfServiceSignupContent() {
  const { data: status } = useSelfServiceStatus();
  const siteKey = status?.turnstile_site_key ?? null;
  const start = useStartSelfServiceSignup();
  const navigate = useNavigate();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const { getFieldProps, touchAll, isValid } = useFormValidation({
    fields: EMAIL_FIELDS,
    values: { email },
  });
  const emailField = getFieldProps('email');

  const { mutateAsync: startSignup } = start;
  const beforeRedirect = useCallback(async () => {
    setError(null);
    await startSignup(turnstileToken);
    try {
      sessionStorage.setItem(OAUTH_RETURN_URL_KEY, FIRST_STEP_PATH);
    } catch {
      // Storage unavailable — ProviderCallback falls back to the default landing.
    }
  }, [startSignup, turnstileToken]);

  const onEmailSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setEmailError(null);
      touchAll();
      if (!isValid || isSubmittingEmail) {
        return;
      }
      const trimmedEmail = email.trim();
      setIsSubmittingEmail(true);
      try {
        // Same arming step the OAuth buttons run: Turnstile + server gate.
        await startSignup(turnstileToken);
        stashEmailVerificationHandoff(SET_PASSWORD_PATH, trimmedEmail);
        const resp = await signUp({ email: trimmedEmail });
        if (resp.status === 401) {
          // Pending verify_email flow (an existing account gets the same shape,
          // to prevent enumeration — that email explains instead). Keep the
          // handoff so the verify screen can route the post-verification login
          // to the set-password step. The global AuthChangeRedirector also
          // routes here; navigating ourselves covers the case where its
          // one-shot FLOW_UPDATED handling already fired.
          navigate(VERIFY_EMAIL_PATH);
          return;
        }
        if (resp.status === 200 && resp.meta?.is_authenticated) {
          // Verification disabled in this environment: the account was still
          // created passwordless, so disabling verification must skip only the
          // code step, NOT the password setup. Go straight to the set-password
          // step. The handoff exists only to bridge the verify screen, which we
          // are bypassing, so clear it.
          clearEmailVerificationHandoff();
          navigate(SET_PASSWORD_PATH);
          return;
        }
        // Any non-401 failure ends this attempt without a pending verify flow,
        // so the stashed handoff is stale — clear it so a later, unrelated
        // verify_email flow is not misrouted to the set-password step.
        clearEmailVerificationHandoff();
        const firstError = resp.errors?.[0]?.message;
        setEmailError(firstError ?? GENERIC_SIGNUP_ERROR);
      } catch {
        // The request failed before any pending verify flow was established;
        // drop the stashed handoff so it cannot misroute a later flow.
        clearEmailVerificationHandoff();
        setEmailError(GENERIC_SIGNUP_ERROR);
      } finally {
        setIsSubmittingEmail(false);
      }
    },
    [
      touchAll,
      isValid,
      isSubmittingEmail,
      email,
      startSignup,
      turnstileToken,
      navigate,
    ]
  );

  const needsToken = siteKey !== null && turnstileToken === null;

  return (
    <div className='flex flex-col gap-8'>
      {/* Header */}
      <div className='flex flex-col gap-3'>
        <h1 className='font-display text-4xl font-medium text-fg-brand-primary'>
          Create your account
        </h1>
        <p className='text-base text-[var(--color-text-tertiary)]'>
          Ahoy starts working before you've finished setting up. Try it out for
          14 days free on the Startup plan.
        </p>
      </div>

      {siteKey !== null && (
        <TurnstileWidget siteKey={siteKey} onToken={setTurnstileToken} />
      )}

      <div className='flex flex-col gap-3'>
        <ProviderList
          callbackURL={OAUTH_CALLBACK_PATH}
          process={AuthProcess.LOGIN}
          getLabel={getProviderButtonLabel}
          isDisabled={needsToken}
          beforeRedirect={beforeRedirect}
          onError={setError}
        />
        {error && (
          <div role='alert'>
            <HintText isInvalid showError>
              {error}
            </HintText>
          </div>
        )}
      </div>

      {/* Separator */}
      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <div className='w-full border-t border-gray-200 dark:border-gray-700' />
        </div>
        <div className='relative flex justify-center'>
          <span className='bg-[var(--color-bg-primary)] px-3 text-sm text-[var(--color-text-tertiary)]'>
            or
          </span>
        </div>
      </div>

      {/* Email signup */}
      <form onSubmit={onEmailSubmit} noValidate className='flex flex-col gap-3'>
        <Input
          type='email'
          name='email'
          aria-label='Work email address'
          placeholder='Enter your work email address'
          autoComplete='email'
          isRequired
          value={email}
          onChange={setEmail}
          isDisabled={needsToken || isSubmittingEmail}
          onBlur={emailField.onBlur}
          isInvalid={emailField.showError}
          showError={emailField.showError}
        />
        {emailField.showError && emailField.error && (
          <HintText isInvalid showError>
            {emailField.error}
          </HintText>
        )}
        <Button
          type='submit'
          color='primary'
          size='md'
          className='w-full'
          isDisabled={needsToken || isSubmittingEmail}
          disabledTooltip={
            needsToken
              ? 'Waiting for the security check to finish'
              : 'Sending your code…'
          }
          isLoading={isSubmittingEmail}
          showTextWhileLoading
        >
          {isSubmittingEmail ? 'Sending your code…' : 'Continue'}
        </Button>
        {emailError && (
          <div role='alert'>
            <HintText isInvalid showError>
              {emailError}
            </HintText>
          </div>
        )}
      </form>

      {/* Trial fine print */}
      <p className='text-sm font-medium text-[var(--color-text-tertiary)]'>
        A card's needed to start your trial – no charge for 14 days. After that,
        $99 per seat/month or $79 billed annually. Cancel any time.
      </p>
    </div>
  );
}
